import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Form, Button, Card } from 'react-bootstrap';
import { FaTrash, FaPlus, FaMinus } from 'react-icons/fa';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { addToCart, removeFromCart } from '../slices/cartSlice';
import {
  useGetStoreSupplyDetailsQuery,
  useUpdateStoreSupplyMutation,
  useUploadStoreSupplyImagesMutation,
} from '../slices/storeSuppliesApiSlice';
import { useGetSuppliersQuery } from '../slices/suppliersApiSlice';
import { toast } from 'react-toastify';

const StoreSupplyEditScreen = () => {
  const { id: storeSupplyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { cartItems } = useSelector((state) => state.cart);

  const [uploadStoreSupplyImages] = useUploadStoreSupplyImagesMutation();
  const [updateStoreSupply, { isLoading: savingStoreSupply }] = useUpdateStoreSupplyMutation();

  const [supplierSearch, setSupplierSearch] = useState('');
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [note, setNote] = useState('');
  const [status, setStatus] = useState('Cash');
  const [images, setImages] = useState([]);

  const [storeSupplyDate, setStoreSupplyDate] = useState(new Date().toISOString().split('T')[0]);
  const [expiryDate, setExpiryDate] = useState(new Date().toISOString().split('T')[0]);

  const [costs, setCosts] = useState({});
  const [totalCost, setTotalCost] = useState(0);

  const [mergedOnce, setMergedOnce] = useState(false);

  const { data: suppliersData } = useGetSuppliersQuery({ keyword: supplierSearch });

  const {
    data: storeSupplyData,
    isLoading: loadingStoreSupply,
    error: storeSupplyError,
    refetch,
  } = useGetStoreSupplyDetailsQuery(storeSupplyId);

  // ------------------------------------------------------------
  // NORMALIZE ITEMS (critical)
  // ------------------------------------------------------------
  const normalizeStoreSupplyCart = (items) =>
    items.map((item) => ({
      ...item,
      _id: item._id || item.product,
      product: item.product || item._id,
      name: item.name || item.productName || 'Unknown',
      price: item.price || 0,
      qty: item.qty || 1,
      images: item.images || [],
      itemCost:
        item.itemCost !== undefined
          ? item.itemCost
          : Math.round((item.qty || 1) * (item.price || 0) * 0.45), // 45% suggestion
    }));

  // ------------------------------------------------------------
  // MERGE STORESUPPLY ITEMS + CART ITEMS
  // ------------------------------------------------------------
  const mergeStoreSupplyWithCart = (storeSupplyItems = [], cartItems = []) => {
    const map = new Map();

    normalizeStoreSupplyCart(cartItems).forEach((item) => {
      map.set(item._id.toString(), { ...item });
    });

    normalizeStoreSupplyCart(storeSupplyItems).forEach((item) => {
      map.set(item._id.toString(), { ...item });
    });

    return Array.from(map.values());
  };

  // ------------------------------------------------------------
  // Load storeSupply + merge ONCE
  // ------------------------------------------------------------
  useEffect(() => {
    if (storeSupplyData && !mergedOnce) {
      setSelectedSupplier(storeSupplyData.supplierDetails || null);
      setNote(storeSupplyData.note || '');
      setStatus(storeSupplyData.status || 'Cash');
      setImages(storeSupplyData.images || []);
      setStoreSupplyDate(storeSupplyData.date?.split('T')[0]);
      setExpiryDate(storeSupplyData.expiryDate?.split('T')[0]);

      const merged = mergeStoreSupplyWithCart(storeSupplyData.storeSupplyItems, cartItems);

      merged.forEach((item) => {
        dispatch(
          addToCart({
            ...item,
            product: item.product,
            qty: item.qty,
            itemCost: item.itemCost,
          })
        );
      });

      setMergedOnce(true);
    }
  }, [storeSupplyData, mergedOnce]);

  // ------------------------------------------------------------
  // Initialize costs (keyed by _id)
  // ------------------------------------------------------------
  useEffect(() => {
    const normalized = normalizeStoreSupplyCart(cartItems);

    const initialCosts = normalized.reduce((acc, item) => {
      acc[item._id] = item.itemCost;
      return acc;
    }, {});

    setCosts(initialCosts);

    const total = Object.values(initialCosts).reduce((acc, cost) => acc + cost, 0);
    setTotalCost(total);
  }, [cartItems]);

  // ------------------------------------------------------------
  // Supplier filtering
  // ------------------------------------------------------------
  useEffect(() => {
    if (suppliersData?.suppliers) {
      const filtered = suppliersData.suppliers.filter((supplier) =>
        supplier.name.toLowerCase().includes(supplierSearch.toLowerCase())
      );
      setFilteredSuppliers(filtered);
    }
  }, [supplierSearch, suppliersData]);

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch(supplier.name);
    setFilteredSuppliers([]);
  };

  // ------------------------------------------------------------
  // Qty handlers
  // ------------------------------------------------------------
  const increaseQtyHandler = (item) => {
    dispatch(addToCart({ ...item, qty: item.qty + 1 }));
  };

  const decreaseQtyHandler = (item) => {
    if (item.qty > 1) {
      dispatch(addToCart({ ...item, qty: item.qty - 1 }));
    } else {
      toast.error('Quantity cannot be less than 1');
    }
  };

  // ------------------------------------------------------------
  // Cost handlers
  // ------------------------------------------------------------
  const adjustCostHandler = (id, adjustment) => {
    const updated = { ...costs };
    updated[id] = Math.max(0, updated[id] + adjustment);
    setCosts(updated);

    const total = Object.values(updated).reduce((acc, cost) => acc + cost, 0);
    setTotalCost(total);
  };

  const removeFromCartHandler = (id) => {
    dispatch(removeFromCart(id));

    const updated = { ...costs };
    delete updated[id];
    setCosts(updated);

    const total = Object.values(updated).reduce((acc, cost) => acc + cost, 0);
    setTotalCost(total);
  };

  // ------------------------------------------------------------
  // Image upload
  // ------------------------------------------------------------
  const uploadImageHandler = async (files) => {
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('images', file));

    try {
      const res = await uploadStoreSupplyImages(formData).unwrap();
      toast.success(res.message);
      setImages([...images, ...res.images]);
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const removeImageHandler = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  // ------------------------------------------------------------
  // Profit + Unit Cost formulas (copied from StoreSupplyForm)
  // ------------------------------------------------------------
  const calculateItemProfit = (totalPrice, totalCost) => {
    return (totalPrice - totalCost).toFixed(2);
  };

  const calculateItemUnitCost = (totalCost, qty) => {
    return qty > 0 ? (totalCost / qty).toFixed(2) : 0;
  };

  // ------------------------------------------------------------
  // Totals (copied from StoreSupplyForm)
  // ------------------------------------------------------------
  const normalizedCart = normalizeStoreSupplyCart(cartItems);

  const totalQuantity = normalizedCart.reduce((acc, item) => acc + item.qty, 0);
  const totalPrice = normalizedCart.reduce((acc, item) => acc + item.qty * item.price, 0);
  const profit = (totalPrice - totalCost).toFixed(2);
  const unitCost = totalQuantity > 0 ? (totalCost / totalQuantity).toFixed(2) : 0;
  const cardCost = (totalPrice - profit).toFixed(2);

  // ------------------------------------------------------------
  // Submit
  // ------------------------------------------------------------
  const updateStoreSupplyHandler = async () => {
    try {
      const storeSupplyItems = normalizedCart.map((item) => ({
        name: item.name,
        qty: item.qty,
        price: item.price,
        itemCost: costs[item._id],
        images: item.images[0],
        product: item._id,
      }));

      await updateStoreSupply({
        storeSupplyId,
        storeSupplyItems,
        supplierDetails: selectedSupplier._id,
        note,
        status,
        date: storeSupplyDate,
        expiryDate,
        images,
      }).unwrap();

      toast.success('StoreSupply updated successfully');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update storeSupply');
    }
  };

  // ------------------------------------------------------------
  // RETURN BLOCK (matches StoreSupplyForm EXACTLY)
  // ------------------------------------------------------------
  return (
    <Row>
      <Col md={8}>
        <h1>Edit StoreSupply</h1>

        {normalizedCart.length === 0 ? (
          <Message>
            Your cart is empty. <Button onClick={() => navigate('/house/HomeScreen')}>Go Back</Button>
          </Message>
        ) : (
          <ListGroup variant="flush">
            {normalizedCart.map((item) => (
              <ListGroup.Item key={item._id}>
                <Row>
                  <Col md={2}>
                    {item.images?.length > 0 && (
                      <img
                        src={item.images[0]}
                        alt="Product Thumbnail"
                        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                      />
                    )}
                  </Col>
                  <Col>{item.name}</Col>
                  <Col md={2}>D{item.price}</Col>

                  {/* +/- BUTTONS (your choice) */}
                  <Col md={2}>
                    <div className="d-flex align-items-center">
                      <Button variant="light" onClick={() => decreaseQtyHandler(item)}>
                        <FaMinus />
                      </Button>
                      <span className="mx-2">{item.qty}</span>
                      <Button variant="light" onClick={() => increaseQtyHandler(item)}>
                        <FaPlus />
                      </Button>
                    </div>
                  </Col>

                  <Col md={2}>D{(item.qty * item.price).toFixed(2)}</Col>

                  <Col md={1}>
                    <Button variant="light" onClick={() => removeFromCartHandler(item._id)}>
                      <FaTrash />
                    </Button>
                  </Col>
                </Row>

                {/* COST ADJUSTMENT */}
                <Row className="mt-2">
                  <Col md={4}>Cost:</Col>
                  <Col md={4}>D{costs[item._id]}</Col>
                  <Col md={4}>
                    <Button variant="light" onClick={() => adjustCostHandler(item._id, -100)}>
                      <FaMinus />
                    </Button>
                    <Button variant="light" onClick={() => adjustCostHandler(item._id, -10)}>
                      <FaMinus />
                    </Button>
                    <Button variant="light" onClick={() => adjustCostHandler(item._id, -1)}>
                      <FaMinus />
                    </Button>
                    <Button variant="light" onClick={() => adjustCostHandler(item._id, 1)}>
                      <FaPlus />
                    </Button>
                    <Button variant="light" onClick={() => adjustCostHandler(item._id, 10)}>
                      <FaPlus />
                    </Button>
                    <Button variant="light" onClick={() => adjustCostHandler(item._id, 100)}>
                      <FaPlus />
                    </Button>
                  </Col>
                </Row>

                {/* PROFIT + UNIT COST */}
                <Row className="mt-2">
                  <Col md={6}>
                    <strong>Profit:</strong> D
                    {calculateItemProfit(item.qty * item.price, costs[item._id])}
                  </Col>
                  <Col md={6}>
                    <strong>Unit Cost:</strong> D
                    {calculateItemUnitCost(costs[item._id], item.qty)}
                  </Col>
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Col>

      {/* RIGHT PANEL */}
      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>Select Supplier</h2>

              <Form.Group controlId="supplierSearch">
                <Form.Label>Search Supplier</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Type supplier name"
                  value={supplierSearch}
                  onChange={(e) => setSupplierSearch(e.target.value)}
                />
              </Form.Group>

              {filteredSuppliers.length > 0 && (
                <ListGroup className="dropdown-menu mt-2">
                  {filteredSuppliers.map((supplier) => (
                    <ListGroup.Item
                      key={supplier._id}
                      action
                      onClick={() => handleSupplierSelect(supplier)}
                    >
                      {supplier.name} ({supplier.company})
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}

              {selectedSupplier && (
                <div className="mt-3">
                  <strong>Selected Supplier:</strong>
                  <p>
                    {selectedSupplier.name} ({selectedSupplier.company})<br />
                    Phone: {selectedSupplier.telephone}<br />
                    Address: {selectedSupplier.address}<br />
                    SERIAL: {selectedSupplier.serialNumber}
                  </p>

                  <Form.Group controlId="note">
                    <Form.Label>Note</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter additional notes"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group controlId="status" className="mt-2">
                    <Form.Label>Status</Form.Label>
                    <Form.Control
                      as="select"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option value="Cash">Cash</option>
                      <option value="Credit">Credit</option>
                      <option value="Donation">Donation</option>
                    </Form.Control>
                  </Form.Group>

                  <Form.Group controlId="storeSupplyDate" className="mt-2">
                    <Form.Label>Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={storeSupplyDate}
                      onChange={(e) => setStoreSupplyDate(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group controlId="expiryDate" className="mt-2">
                    <Form.Label>Expiry Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                    />
                  </Form.Group>

                  {/* IMAGE UPLOAD */}
                  <Form.Group controlId="images" className="mb-3">
                    <Form.Label>Images</Form.Label>
                    <Form.Control
                      type="file"
                      multiple
                      onChange={(e) => uploadImageHandler(e.target.files)}
                    />

                    <div className="mt-3">
                      {images.map((img, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: 'inline-block',
                            position: 'relative',
                            marginRight: '10px',
                          }}
                        >
                          <img
                            src={img}
                            alt={`Uploaded ${idx}`}
                            className="img-fluid"
                            style={{ maxWidth: '100px', height: '100px', objectFit: 'cover' }}
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removeImageHandler(idx)}
                            style={{
                              position: 'absolute',
                              top: '0',
                              right: '0',
                              borderRadius: '50%',
                              padding: '2px 5px',
                            }}
                          >
                            ✖
                          </Button>
                        </div>
                      ))}
                    </div>
                  </Form.Group>
                </div>
              )}
            </ListGroup.Item>

            {/* TOTAL SUMMARY */}
            <ListGroup.Item>
              <h3>Total Summary:</h3>

              <p><strong>Total Quantity:</strong> {totalQuantity}</p>
              <p><strong>Total Price:</strong> D{totalPrice}</p>
              <p><strong>Total Cost:</strong> D{totalCost}</p>
              <p><strong>Profit:</strong> D{profit}</p>
              <p><strong>Unit Cost:</strong> D{unitCost}</p>
              <p><strong>Card Cost:</strong> D{cardCost}</p>
            </ListGroup.Item>

            <ListGroup.Item>
              <Button
                type="button"
                className="btn-block"
                disabled={normalizedCart.length === 0 || !selectedSupplier}
                onClick={updateStoreSupplyHandler}
              >
                Update StoreSupply
              </Button>

              {savingStoreSupply && <Loader />}
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default StoreSupplyEditScreen;
